/**
 * Relatos client store — identity, memberships, subscriptions, actions.
 * Membership epochKeys maps are append-only (CLAUDE.md §3: backlog stays readable
 * with old keys; only the current epoch key is ever distributed).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { NostrEvent, EventTemplate } from 'nostr-tools';
import { nip19, getPublicKey } from 'nostr-tools';
import { bytesToHex, randomBytes, hexToBytes } from '@noble/hashes/utils.js';
import {
  generateBoardKey, rotateEpoch, deriveEpochKey, generatePseudoKey,
  createInvite, redeemInvite, decodeInvite, encodeInvite,
  createInviteV2, redeemInviteV2, encodeInviteV2, decodeInviteV2, peerDecrypt,
  blindBoard, type BoardMeta,
} from './crypto';
import {
  KIND_BOARD, KIND_POST, KIND_REACTION, KIND_DELETE, KIND_KEYUPDATE, KIND_GIFTWRAP, KIND_JOIN, KIND_PRESENCE, KIND_RELAYLIST,
  DEFAULT_RELAYS, isForumAdmin, MODERATOR_PUBKEY, type PostPayload, type KeyUpdatePayload, type ReportPayload,
} from './kinds';
import {
  buildBoardDef, readBoardDef, buildPost, readPost, buildReaction, readReaction,
  buildDelete, readDelete, readJoin,
  buildJoin, buildPresence, buildKeyUpdate, readKeyUpdate, buildKeyUpdateAsync, readKeyUpdateAsync,
  buildResyncReq, buildResyncReqAsync, buildResyncKeys, buildResyncKeysAsync, readResync, readResyncAsync,
  buildReport, buildReportAsync, readReport, readReportAsync,
  buildRelayList, readRelayList,
} from './events';
import { encryptKey, decryptKey, isEncryptedKey } from './crypto/nip49';
import { shouldRespondToResync } from './resync-policy';
import {
  pool, publish, subscribe, query, sign, generateSigner, nsecSigner, nip07Signer,
  hasNip07, signerCrypto, type Signer,
} from './client';
import { SEED_EVENTS } from './seed';

// --- persisted shapes ------------------------------------------------------

export interface Membership {
  boardId: string;
  admin: string;              // admin's IDENTITY pubkey (def author / invite `adm`)
  adminPseudo?: string;       // admin's per-board posting pubkey (from board meta)
  role: 'admin' | 'member';
  currentEpoch: number;
  epochKeys: Record<number, string>;
  meta: BoardMeta | null;
  relays?: string[];        // board relay list from admin (kind 30002); union'ed with defaults
  relaysUpdatedAt?: number; // updatedAt of the last applied relay list
  pseudoSk?: string;        // my per-board pseudonymous posting key (never leaves this device)
  salts?: Record<number, string>;        // admin only: salt that derived each epoch (resync source)
  memberIndex?: Record<string, string>;  // admin view: pseudo pubkey -> identity pubkey (from join payloads)
  removed?: string[];       // admin: identity pubkeys removed (resync denylist)
}

/** My pseudonymous posting pubkey for a board (identity pk for legacy memberships). */
export function boardPkOf(m: Membership | undefined): string | null {
  if (!m) return null;
  return m.pseudoSk ? getPublicKey(hexToBytes(m.pseudoSk)) : null;
}

/** True if `pk` is the board admin — by identity (defs/relay lists) or by
 *  their per-board pseudonymous key (in-board actions like deletes). */
export function isAdminPk(m: Membership | undefined, pk: string): boolean {
  return !!m && (pk === m.admin || (!!m.adminPseudo && pk === m.adminPseudo));
}

interface PostItem { ev: NostrEvent; payload: PostPayload; epoch: number }

export interface BoardData {
  posts: PostItem[];
  reactions: Record<string, string[]>; // targetId -> pubkeys who reacted
  deleted: Record<string, string>;     // targetId -> pubkey requesting deletion (author or admin)
  roster: Record<string, number>;      // pubkey -> first-seen ts
  online: Record<string, number>;      // pubkey -> last presence ts
  loading: boolean;
}

interface Profile { name?: string; picture?: string; nip05?: string }

const LS_KEY = 'relatos.v1';
const LS_PROFILES = 'relatos.profiles';

export interface PublicBoard {
  boardId: string;
  meta: BoardMeta;
  key: string;   // epoch-1 key, published in the clear by the board def
  admin: string;
}

/** Parse a public board def (kind 34553 with a cleartext ['key', …] tag). */
function parsePublicBoard(ev: NostrEvent): PublicBoard | null {
  const key = ev.tags.find(t => t[0] === 'key')?.[1];
  const d = ev.tags.find(t => t[0] === 'd')?.[1];
  if (!key || !d) return null;
  try {
    const r = readBoardDef(ev, { 1: key });
    return r ? { boardId: d, meta: r.meta, key, admin: ev.pubkey } : null;
  } catch { return null; }
}

interface Persisted {
  sk: string | null;          // NIP-49 ncryptsec string (legacy: raw hex); null when using NIP-07
  pubkey?: string;            // expected pubkey — lets unlock detect a wrong passphrase
  nip07: boolean;
  boards: Record<string, Membership>;
  relays: string[];           // custom default relays (union'ed with DEFAULT_RELAYS)
}

function loadPersisted(): Persisted {
  try {
    const p = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
    if (p && typeof p === 'object') return { sk: null, nip07: false, boards: {}, relays: [], ...p };
  } catch { /* corrupted storage -> fresh start */ }
  return { sk: null, nip07: false, boards: {}, relays: [] };
}

function savePersisted(p: Persisted) {
  localStorage.setItem(LS_KEY, JSON.stringify(p));
}

// --- context ---------------------------------------------------------------

export interface Store {
  signer: Signer | null;
  boards: Record<string, Membership>;
  data: Record<string, BoardData>;
  profiles: Record<string, Profile>;
  relays: string[];
  customRelays: string[];
  relayHealth: Record<string, boolean>; // relay url -> connected (SimplePool status)
  publicBoards: Record<string, PublicBoard>;
  /** Moderator inbox: decrypted post reports (populated only for MODERATOR_PUBKEY's wraps). */
  reports: Array<{ report: ReportPayload; from: string; at: number }>;
  ready: boolean;
  locked: boolean;          // NIP-49: encrypted key stored, passphrase needed
  keyIsPlaintext: boolean;  // legacy raw-hex key at rest — offer upgrade
  loginWithNip07(): Promise<void>;
  loginWithNsec(nsec: string, password: string): void;
  createIdentity(password: string): { nsec: string };
  unlockWithPassphrase(password: string): void;
  encryptStoredKey(password: string): void;
  logout(): void;
  createBoard(name: string, description: string, isPublic?: boolean): Promise<string>;
  makeInvite(boardId: string, inviteeNpub: string, ttlHours: number): Promise<string>;
  joinWithInvite(link: string): Promise<string>;
  joinPublicBoard(boardId: string): Promise<void>;
  postThread(boardId: string, subject: string, body: string): Promise<void>;
  postReply(boardId: string, rootId: string, parentId: string, body: string): Promise<void>;
  react(boardId: string, targetId: string): Promise<void>;
  deletePost(boardId: string, targetId: string): Promise<void>;
  removeMember(boardId: string, memberPk: string): Promise<void>;
  requestResync(boardId: string): Promise<void>;
  reportPost(boardId: string, target: { id: string; pubkey: string }, reason: string, content?: { subject?: string; body?: string }): Promise<void>;
  publishRelayList(boardId: string, relays: string[]): Promise<void>;
  addRelay(url: string): void;
  removeRelay(url: string): void;
  ensureProfiles(pks: string[]): void;
}

const Ctx = createContext<Store | null>(null);
export const useStore = () => {
  const s = useContext(Ctx);
  if (!s) throw new Error('store missing');
  return s;
};

const EMPTY_DATA: BoardData = { posts: [], reactions: {}, deleted: {}, roster: {}, online: {}, loading: true };

export function StoreProvider({ children }: { children: React.ReactNode }) {
  const [persisted, setPersisted] = useState<Persisted>(loadPersisted);
  const [signer, setSigner] = useState<Signer | null>(null);
  const [data, setData] = useState<Record<string, BoardData>>({});
  const [profiles, setProfiles] = useState<Record<string, Profile>>(() => {
    try { return JSON.parse(localStorage.getItem(LS_PROFILES) || '{}'); } catch { return {}; }
  });
  const [ready, setReady] = useState(false);
  const [locked, setLocked] = useState(false); // encrypted key stored, awaiting passphrase
  const [relayHealth, setRelayHealth] = useState<Record<string, boolean>>({});
  // Moderator inbox: decrypted post reports (session-only; export/copy what you keep)
  const [reports, setReports] = useState<Array<{ report: ReportPayload; from: string; at: number }>>([]);
  // Seed boards show instantly; relay-discovered public boards merge on top.
  const [publicBoards, setPublicBoards] = useState<Record<string, PublicBoard>>(() => {
    const out: Record<string, PublicBoard> = {};
    for (const ev of SEED_EVENTS) {
      const b = parsePublicBoard(ev);
      if (b) out[b.boardId] = b;
    }
    return out;
  });
  const relays = useMemo(
    () => [...new Set([...DEFAULT_RELAYS, ...persisted.relays])],
    [persisted.relays],
  );
  const subsRef = useRef<Record<string, () => void>>({});
  const subscribedRef = useRef<Set<string>>(new Set());
  const presenceTimer = useRef<number | null>(null);
  const persistedRef = useRef(persisted);
  persistedRef.current = persisted;
  const dataRef = useRef(data);
  dataRef.current = data;
  const requestResyncRef = useRef<(boardId: string) => void>(() => {});

  const persist = useCallback((fn: (p: Persisted) => Persisted) => {
    setPersisted(prev => {
      const next = fn(prev);
      savePersisted(next);
      return next;
    });
  }, []);

  /** Effective relay set for a board = union(default+custom relays, membership.relays). */
  const relaysFor = useCallback(
    (m?: Membership) => [...new Set([...relays, ...(m?.relays ?? [])])],
    [relays],
  );

  /** Membership with a guaranteed per-board pseudonymous key (lazily created
   *  for pre-upgrade memberships). */
  const withPseudo = useCallback((boardId: string): Membership => {
    const m = persistedRef.current.boards[boardId];
    if (!m) throw new Error('Not a member');
    if (m.pseudoSk) return m;
    const g = generatePseudoKey();
    const mm = { ...m, pseudoSk: g.sk };
    persist(p => ({ ...p, boards: { ...p.boards, [boardId]: { ...p.boards[boardId], pseudoSk: g.sk } } }));
    return mm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist]);

  /** Signer over my per-board pseudonymous key — in-board events never carry
   *  the identity pubkey, so relays cannot correlate authors across boards. */
  const boardSigner = useCallback((m: Membership): Signer & { type: 'local' } => {
    if (!m.pseudoSk) throw new Error('No board posting key');
    return { type: 'local', sk: m.pseudoSk, pubkey: getPublicKey(hexToBytes(m.pseudoSk)) };
  }, []);

  // restore identity on load
  useEffect(() => {
    (async () => {
      const p = loadPersisted();
      try {
        if (p.nip07 && hasNip07()) {
          setSigner(await nip07Signer());
        } else if (p.sk && isEncryptedKey(p.sk)) {
          setLocked(true); // NIP-49: key is passphrase-encrypted; LoginPanel unlocks it
        } else if (p.sk) {
          setSigner({ type: 'local', sk: p.sk, pubkey: getPublicKey(hexToBytes(p.sk)) });
        }
      } catch { /* fall through to logged-out */ }
      setReady(true);
    })();
  }, []);

  // --- ingest ----------------------------------------------------------------
  const ingest = useCallback((boardId: string, ev: NostrEvent, m: Membership) => {
    setData(prev => {
      const cur = prev[boardId] ?? { ...EMPTY_DATA };
      if (ev.kind === KIND_POST) {
        if (cur.posts.some(p => p.ev.id === ev.id)) return prev;
        const r = readPost(ev, boardId, m.epochKeys);
        if (!r) return prev;
        const posts = [...cur.posts, { ev, payload: r.payload, epoch: r.epoch }];
        return { ...prev, [boardId]: { ...cur, loading: false, posts, roster: { ...cur.roster, [ev.pubkey]: cur.roster[ev.pubkey] ?? ev.created_at } } };
      }
      if (ev.kind === KIND_REACTION) {
        const p = readReaction(ev, boardId, m.epochKeys);
        if (!p) return prev;
        const list = cur.reactions[p.target] ?? [];
        if (list.includes(ev.pubkey)) return prev;
        return { ...prev, [boardId]: { ...cur, reactions: { ...cur.reactions, [p.target]: [...list, ev.pubkey] } } };
      }
      if (ev.kind === KIND_DELETE) {
        const p = readDelete(ev, boardId, m.epochKeys);
        if (!p) return prev;
        // honor deletes from the post author or the board admin only
        const post = cur.posts.find(x => x.ev.id === p.target);
        const allowed = post ? (ev.pubkey === post.ev.pubkey || isAdminPk(m, ev.pubkey)) : true; // post may arrive later; re-check at render
        if (!allowed) return prev;
        return { ...prev, [boardId]: { ...cur, deleted: { ...cur.deleted, [p.target]: ev.pubkey } } };
      }
      if (ev.kind === KIND_JOIN) {
        return { ...prev, [boardId]: { ...cur, roster: { ...cur.roster, [ev.pubkey]: cur.roster[ev.pubkey] ?? ev.created_at } } };
      }
      if (ev.kind === KIND_PRESENCE) {
        return { ...prev, [boardId]: { ...cur, online: { ...cur.online, [ev.pubkey]: ev.created_at } } };
      }
      return prev;
    });
  }, []);

  const subscribeBoard = useCallback((m: Membership) => {
    const tag = m.boardId + ':' + m.currentEpoch + ':' + (m.relays ?? []).join(',');
    if (subscribedRef.current.has(tag)) return;
    subscribedRef.current.add(tag);

    const boardRelays = relaysFor(m);
    const blinds = Object.values(m.epochKeys).map(k => blindBoard(k, m.boardId));
    const close = subscribe(boardRelays, [
      { kinds: [KIND_BOARD], '#d': [m.boardId] },
      { kinds: [KIND_RELAYLIST], '#d': [`relatos:${m.boardId}`] },
      { kinds: [KIND_POST, KIND_REACTION, KIND_JOIN, KIND_DELETE], '#h': blinds },
      { kinds: [KIND_PRESENCE], '#h': blinds },
    ], ev => {
      if (ev.kind === KIND_BOARD) {
        persist(p => {
          const mm = p.boards[m.boardId];
          if (!mm) return p;
          const r = readBoardDef(ev, mm.epochKeys);
          if (!r) return p;
          return { ...p, boards: { ...p.boards, [m.boardId]: { ...mm, meta: r.meta, adminPseudo: r.meta.adminPk ?? mm.adminPseudo } } };
        });
        return;
      }
      if (ev.kind === KIND_JOIN) {
        // Join payloads carry the member's identity pubkey inside the ciphertext;
        // the admin (any member, really) learns pseudo -> identity, relays see nothing.
        const jp = readJoin(ev, m.boardId, m.epochKeys);
        if (jp?.realPk && /^[0-9a-f]{64}$/.test(jp.realPk)) {
          persist(p => {
            const mm = p.boards[m.boardId];
            if (!mm) return p;
            const memberIndex = { ...(mm.memberIndex ?? {}), [ev.pubkey]: jp.realPk! };
            return { ...p, boards: { ...p.boards, [m.boardId]: { ...mm, memberIndex } } };
          });
        }
      }
      if (ev.kind === KIND_RELAYLIST) {
        persist(p => {
          const mm = p.boards[m.boardId];
          if (!mm) return p;
          const r = readRelayList(ev, mm.epochKeys, mm.admin);
          if (!r || r.updatedAt <= (mm.relaysUpdatedAt ?? 0)) return p;
          return { ...p, boards: { ...p.boards, [m.boardId]: { ...mm, relays: r.relays, relaysUpdatedAt: r.updatedAt } } };
        });
        return;
      }
      ingest(m.boardId, ev, m);
    });
    subsRef.current[tag] = close;
  }, [ingest, persist, relaysFor]);

  /**
   * Salt resync (review: a member offline during a rotation was silently and
   * permanently locked out). Two roles:
   * - Admin receiving a resync-req: sender must be a known, non-removed member
   *   (identity pubkey in memberIndex, or a legacy identity seen in the roster);
   *   reply with every salt above their epoch, gift-wrapped. Automatic.
   * - Member receiving resync-keys: only from the board admin; apply the salt
   *   chain strictly in order, stopping at the first gap.
   */
  const handleResync = useCallback(async (
    s: Signer,
    crypto: ReturnType<typeof signerCrypto>,
    r: NonNullable<Awaited<ReturnType<typeof readResync>> | Awaited<ReturnType<typeof readResyncAsync>>>,
  ) => {
    const cur = persistedRef.current;
    if ('req' in r) {
      const { req, from } = r;
      const mm = cur.boards[req.board];
      // Membership gate FIRST (review flag #1): a kicked member who still holds
      // old keys must never receive new salts — that would defeat removal-by-rotation.
      const inRoster = !!dataRef.current[req.board]?.roster?.[from]; // legacy identity posters
      if (!shouldRespondToResync(mm, from, req.haveEpoch, inRoster)) return;
      const salts = Object.fromEntries(
        Object.entries(mm.salts ?? {}).filter(([e]) => Number(e) > req.haveEpoch && Number(e) <= mm.currentEpoch),
      );
      if (Object.keys(salts).length === 0) return; // pre-upgrade board: no salt history stored
      const m = relaysFor(mm);
      let ev: NostrEvent;
      if (s.type === 'local') {
        ev = buildResyncKeys(s.sk, from, req.board, salts);
      } else {
        if (!crypto) return;
        ev = await buildResyncKeysAsync(crypto, t => sign(s, t), s.pubkey, from, req.board, salts);
      }
      await publish(m, ev).catch(() => undefined);
      return;
    }
    const { keys, from } = r;
    if (from !== cur.boards[keys.board]?.admin) return; // only the board admin may hand out salts
    persist(prev => {
      const mm = prev.boards[keys.board];
      if (!mm) return prev;
      const entries = Object.entries(keys.salts)
        .map(([e, salt]) => [Number(e), salt] as const)
        .sort((a, b) => a[0] - b[0]);
      const epochKeys = { ...mm.epochKeys };
      let currentEpoch = mm.currentEpoch;
      let changed = false;
      for (const [e, salt] of entries) {
        if (e !== currentEpoch + 1 || !epochKeys[currentEpoch]) break; // contiguous chain only
        epochKeys[e] = deriveEpochKey(epochKeys[currentEpoch], salt, e).key;
        currentEpoch = e;
        changed = true;
      }
      if (!changed) return prev;
      return { ...prev, boards: { ...prev.boards, [keys.board]: { ...mm, currentEpoch, epochKeys } } };
    });
  }, [persist, relaysFor]);

  // (re)subscribe when memberships/epochs/relay-lists change
  const boardsKey = JSON.stringify([
    Object.values(persisted.boards).map(b => [b.boardId, b.currentEpoch, b.relays]),
    persisted.relays,
  ]);
  useEffect(() => {
    if (!signer) return;
    for (const m of Object.values(persisted.boards)) {
      setData(prev => prev[m.boardId] ? prev : { ...prev, [m.boardId]: { ...EMPTY_DATA } });
      subscribeBoard(m);
    }
    // epoch key updates + salt resync, addressed to me (admin rotates on member
    // removal). NIP-59 gift wraps (kind 1059) hide the admin→member link from
    // relays; legacy cleartext kind 1114 still accepted for pre-upgrade boards.
    const closeKey = subscribe(relays, [{ kinds: [KIND_KEYUPDATE, KIND_GIFTWRAP], '#p': [signer.pubkey] }], ev => {
      void (async () => {
        try {
          const crypto = signerCrypto(signer);
          let p: KeyUpdatePayload | null = null;
          let from = '';
          if (ev.kind === KIND_GIFTWRAP) {
            // try keyupdate, then resync request/response, then moderator reports
            let r1: { payload: KeyUpdatePayload; adminPubkey: string } | null = null;
            let r2: Awaited<ReturnType<typeof readResync>> | Awaited<ReturnType<typeof readResyncAsync>> = null;
            let r3: { report: ReportPayload; from: string } | null = null;
            if (signer.type === 'local') {
              r1 = readKeyUpdate(signer.sk, ev);
              if (!r1) r2 = readResync(signer.sk, ev);
              if (!r1 && !r2) r3 = readReport(signer.sk, ev);
            } else if (crypto) {
              r1 = await readKeyUpdateAsync(crypto, ev);
              if (!r1) r2 = await readResyncAsync(crypto, ev);
              if (!r1 && !r2) r3 = await readReportAsync(crypto, ev);
            }
            if (r2) { await handleResync(signer, crypto, r2); return; }
            if (r3) {
              setReports(prev => [...prev, { ...r3, at: Date.now() }].slice(-200));
              return;
            }
            if (!r1) return;
            p = r1.payload; from = r1.adminPubkey;
          } else {
            if (signer.type !== 'local') return; // legacy cleartext path needs the raw key
            p = peerDecrypt<KeyUpdatePayload>(signer.sk, ev.pubkey, ev.content);
            from = ev.pubkey;
          }
          if (!p || p.t !== 'keyupdate') return;
          const payload = p;
          const cur = persistedRef.current.boards[payload.board];
          if (cur && payload.epoch > cur.currentEpoch + 1 && from === cur.admin) {
            // missed at least one rotation (offline) — deriving from the current
            // key would produce a WRONG key; ask the admin for the salt chain.
            requestResyncRef.current(payload.board);
            return;
          }
          persist(prev => {
            const mm = prev.boards[payload.board];
            if (!mm || payload.epoch !== mm.currentEpoch + 1) return prev;
            if (from !== mm.admin) return prev; // only the board admin may rotate the epoch
            const newKey = deriveEpochKey(mm.epochKeys[mm.currentEpoch], payload.salt, payload.epoch);
            return {
              ...prev,
              boards: {
                ...prev.boards,
                [payload.board]: { ...mm, currentEpoch: payload.epoch, epochKeys: { ...mm.epochKeys, [payload.epoch]: newKey.key } },
              },
            };
          });
        } catch { /* not for us / malformed */ }
      })();
    });
    return () => closeKey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsKey, signer?.pubkey]);

  // presence heartbeat (ephemeral, session-only)
  useEffect(() => {
    if (!signer) return;
    const beat = async () => {
      for (const m0 of Object.values(persisted.boards)) {
        try {
          const m = withPseudo(m0.boardId);
          const key = m.epochKeys[m.currentEpoch];
          const ev = await sign(boardSigner(m), buildPresence(key, m.boardId) as EventTemplate);
          pool.publish(relaysFor(m), ev);
        } catch { /* presence is best-effort */ }
      }
    };
    beat();
    presenceTimer.current = window.setInterval(beat, 60_000);
    return () => { if (presenceTimer.current) window.clearInterval(presenceTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardsKey, signer?.pubkey]);

  // relay health: poll SimplePool connection status for every relay in use
  const allRelaysKey = JSON.stringify([...new Set([...relays, ...Object.values(persisted.boards).flatMap(b => b.relays ?? [])])].sort());
  useEffect(() => {
    const tick = async () => {
      const next: Record<string, boolean> = {};
      await Promise.all((JSON.parse(allRelaysKey) as string[]).map(async (r) => {
        try {
          // ensureRelay actually initiates a connection; listConnectionStatus()
          // only knows relays the pool has already touched (and keys it by
          // normalized URL), which made healthy relays report as down.
          const relay = await Promise.race([
            pool.ensureRelay(r, { connectionTimeout: 4000 }),
            new Promise<never>((_, rej) => setTimeout(() => rej(new Error('timeout')), 6000)),
          ]);
          next[r] = relay.connected;
        } catch {
          next[r] = false;
        }
      }));
      setRelayHealth(next);
    };
    tick();
    const t = window.setInterval(tick, 5000);
    return () => window.clearInterval(t);
  }, [allRelaysKey]);

  // public board directory: publish the pre-signed seed defs (idempotent —
  // relays dedupe by id) and discover other public boards via the t tag.
  useEffect(() => {
    if (!ready) return;
    const seedIds = new Set(SEED_EVENTS.map(e => e.id));
    for (const ev of SEED_EVENTS) publish(relays, ev).catch(() => undefined);
    const closer = subscribe(relays, [{ kinds: [KIND_BOARD], '#t': ['relatos-public'] }], (ev) => {
      // Only forum-admin-created public boards count — anyone can tag an event,
      // so the directory filters out non-admin spam.
      if (!seedIds.has(ev.id) && !isForumAdmin(ev.pubkey)) return;
      const b = parsePublicBoard(ev);
      if (b) setPublicBoards(prev => (prev[b.boardId] ? prev : { ...prev, [b.boardId]: b }));
    });
    return closer;
  }, [ready, relays]);

  // profiles (kind 0), cached in localStorage
  const profilesRef = useRef(profiles);
  profilesRef.current = profiles;
  const ensureProfiles = useCallback((pks: string[]) => {
    const missing = pks.filter(pk => !profilesRef.current[pk]);
    if (missing.length === 0) return;
    query(relays, { kinds: [0], authors: missing }).then(evs => {
      setProfiles(prev => {
        const next = { ...prev };
        for (const ev of evs) {
          try { next[ev.pubkey] = JSON.parse(ev.content); } catch { /* bad profile json */ }
        }
        localStorage.setItem(LS_PROFILES, JSON.stringify(next));
        return next;
      });
    }).catch(() => undefined);
  }, [relays]);

  // --- auth actions ----------------------------------------------------------
  const loginWithNip07 = useCallback(async () => {
    const s = await nip07Signer();
    setSigner(s);
    persist(p => ({ ...p, nip07: true, sk: null }));
  }, [persist]);

  const loginWithNsec = useCallback((nsec: string, password: string) => {
    const s = nsecSigner(nsec);
    if (s.type !== 'local') return;
    setSigner(s);
    setLocked(false);
    persist(p => ({ ...p, nip07: false, sk: encryptKey(s.sk, password), pubkey: s.pubkey })); // NIP-49 at rest
  }, [persist]);

  const createIdentity = useCallback((password: string) => {
    const g = generateSigner();
    setSigner({ type: 'local', sk: g.sk, pubkey: g.pubkey });
    setLocked(false);
    persist(p => ({ ...p, nip07: false, sk: encryptKey(g.sk, password), pubkey: g.pubkey })); // NIP-49 at rest
    return { nsec: g.nsec };
  }, [persist]);

  /** NIP-49: unlock a passphrase-encrypted stored key. */
  const unlockWithPassphrase = useCallback((password: string) => {
    const p = loadPersisted();
    if (!p.sk || !isEncryptedKey(p.sk)) throw new Error('No encrypted key stored');
    const sk = decryptKey(p.sk, password);
    const pubkey = getPublicKey(hexToBytes(sk));
    // XChaCha20 has no MAC — a wrong passphrase decrypts to a garbage key.
    // Compare against the stored expected pubkey to detect that.
    if (p.pubkey && pubkey !== p.pubkey) throw new Error('Wrong passphrase');
    setSigner({ type: 'local', sk, pubkey });
    setLocked(false);
  }, []);

  /** NIP-49: upgrade a legacy plaintext stored key to ncryptsec. */
  const encryptStoredKey = useCallback((password: string) => {
    const s = requireSigner();
    if (s.type !== 'local') throw new Error('NIP-07 keys live in your extension — nothing to encrypt here');
    persist(p => ({ ...p, sk: encryptKey(s.sk, password), pubkey: s.pubkey }));
  }, [signer, persist]);

  const logout = useCallback(() => {
    Object.values(subsRef.current).forEach(c => c());
    subsRef.current = {};
    subscribedRef.current.clear();
    setSigner(null);
    setData({});
    persist(p => ({ ...p, sk: null, nip07: false }));
  }, [persist]);

  // --- board actions ---------------------------------------------------------
  const requireSigner = () => {
    if (!signer) throw new Error('Log in first');
    return signer;
  };

  const createBoard = useCallback(async (name: string, description: string, isPublic = false) => {
    const s = requireSigner();
    if (isPublic && !isForumAdmin(s.pubkey)) {
      throw new Error('Only forum admins can create public boards');
    }
    const boardId = bytesToHex(randomBytes(32));
    const g = generateBoardKey();
    const pseudo = generatePseudoKey();
    const meta: BoardMeta = { name, description, createdAt: Math.floor(Date.now() / 1000), adminPk: pseudo.pk };
    const ev = await sign(s, buildBoardDef(boardId, g.key, meta, isPublic));
    await publish(relays, ev);
    // join announcement is signed by the per-board pseudo key, identity inside ciphertext
    const bs: Signer = { type: 'local', sk: pseudo.sk, pubkey: pseudo.pk };
    const join = await sign(bs, buildJoin(g.key, boardId, 1, undefined, s.pubkey));
    await publish(relays, join).catch(() => undefined);
    persist(p => ({
      ...p,
      boards: {
        ...p.boards,
        [boardId]: {
          boardId, admin: s.pubkey, adminPseudo: pseudo.pk, role: 'admin', currentEpoch: 1,
          epochKeys: { 1: g.key }, meta, pseudoSk: pseudo.sk,
          memberIndex: { [pseudo.pk]: s.pubkey },
        },
      },
    }));
    return boardId;
  }, [signer, persist, relays]);

  /** Join a public board — no invite needed; the key comes from the board def. */
  const joinPublicBoard = useCallback(async (boardId: string) => {
    const s = requireSigner();
    const b = publicBoards[boardId];
    if (!b) throw new Error('Unknown public board');
    if (persisted.boards[boardId]) return;
    const pseudo = generatePseudoKey();
    persist(p => ({
      ...p,
      boards: {
        ...p.boards,
        [boardId]: {
          boardId,
          admin: b.admin,
          adminPseudo: b.meta.adminPk,
          role: s.pubkey === b.admin ? 'admin' as const : 'member' as const,
          currentEpoch: 1,
          epochKeys: { 1: b.key },
          meta: b.meta,
          pseudoSk: pseudo.sk,
          memberIndex: { [pseudo.pk]: s.pubkey },
        },
      },
    }));
    const bs: Signer = { type: 'local', sk: pseudo.sk, pubkey: pseudo.pk };
    const join = await sign(bs, buildJoin(b.key, boardId, 1, undefined, s.pubkey));
    await publish(relays, join).catch(() => undefined);
  }, [signer, publicBoards, persisted.boards, persist, relays]);

  /** Returns the full invite link (token bound to invitee pubkey).
   *  Local keys use the v1 Schnorr-signed token; extension signers use the v2
   *  event-shaped token (signed via signEvent, encrypted via nip44). */
  const makeInvite = useCallback(async (boardId: string, inviteeNpub: string, ttlHours: number) => {
    const s = requireSigner();
    const m = persisted.boards[boardId];
    if (!m || m.role !== 'admin') throw new Error('Only the board admin can invite');
    const dec = nip19.decode(inviteeNpub.trim());
    const inviteePk = dec.type === 'npub' ? dec.data as string
      : dec.type === 'nprofile' ? (dec.data as { pubkey: string }).pubkey
      : (() => { throw new Error('Paste an npub… or nprofile…'); })();
    const params = {
      boardId,
      epoch: m.currentEpoch,
      epochKey: m.epochKeys[m.currentEpoch],
      inviteePk,
      expiresAt: Math.floor(Date.now() / 1000) + ttlHours * 3600,
    };
    if (s.type === 'local') {
      return encodeInvite(createInvite({ adminSk: s.sk, ...params }));
    }
    const crypto = signerCrypto(s);
    if (!crypto) throw new Error('Invites need peer encryption — your signer extension has no nip44 support');
    return encodeInviteV2(await createInviteV2({ crypto, signEvent: t => sign(s, t), ...params }));
  }, [signer, persisted.boards]);

  const joinWithInvite = useCallback(async (link: string) => {
    const s = requireSigner();
    const tokenStr = link.includes('relatos') ? link.slice(link.indexOf('relatos')) : link.trim();
    let grant: { boardId: string; adminPubkey: string; epoch: number; epochKey: string };
    if (tokenStr.startsWith('relatos2')) {
      const crypto = signerCrypto(s);
      if (!crypto) throw new Error('Redeeming this invite needs peer encryption — your signer has no nip44 support');
      grant = await redeemInviteV2(crypto, s.pubkey, decodeInviteV2(tokenStr.trim()));
    } else {
      if (s.type !== 'local') throw new Error('Redeeming legacy invites needs a local key (paste an nsec or generate one)');
      grant = redeemInvite(s.sk, decodeInvite(tokenStr.trim()));
    }
    const pseudo = generatePseudoKey();
    persist(p => ({
      ...p,
      boards: {
        ...p.boards,
        [grant.boardId]: {
          boardId: grant.boardId,
          admin: grant.adminPubkey,
          role: s.pubkey === grant.adminPubkey ? 'admin' : 'member',
          currentEpoch: grant.epoch,
          epochKeys: { [grant.epoch]: grant.epochKey },
          meta: null,
          pseudoSk: pseudo.sk,
          memberIndex: { [pseudo.pk]: s.pubkey },
        },
      },
    }));
    const bs: Signer = { type: 'local', sk: pseudo.sk, pubkey: pseudo.pk };
    const join = await sign(bs, buildJoin(grant.epochKey, grant.boardId, grant.epoch, undefined, s.pubkey));
    await publish(relays, join).catch(() => undefined);
    return grant.boardId;
  }, [signer, persist, relays]);

  const postThread = useCallback(async (boardId: string, subject: string, body: string) => {
    requireSigner();
    const m = withPseudo(boardId);
    const key = m.epochKeys[m.currentEpoch];
    const ev = await sign(boardSigner(m), buildPost(key, boardId, m.currentEpoch, { t: 'post', kind: 'thread', subject, body }));
    await publish(relaysFor(m), ev);
    ingest(boardId, ev, m);
  }, [withPseudo, boardSigner, relaysFor, ingest]);

  const postReply = useCallback(async (boardId: string, rootId: string, parentId: string, body: string) => {
    requireSigner();
    const m = withPseudo(boardId);
    const key = m.epochKeys[m.currentEpoch];
    const ev = await sign(boardSigner(m), buildPost(key, boardId, m.currentEpoch, { t: 'post', kind: 'reply', root: rootId, parent: parentId, body }));
    await publish(relaysFor(m), ev);
    ingest(boardId, ev, m);
  }, [withPseudo, boardSigner, relaysFor, ingest]);

  const react = useCallback(async (boardId: string, targetId: string) => {
    requireSigner();
    const m = withPseudo(boardId);
    const key = m.epochKeys[m.currentEpoch];
    const ev = await sign(boardSigner(m), buildReaction(key, boardId, m.currentEpoch, targetId));
    await publish(relaysFor(m), ev);
    ingest(boardId, ev, m);
  }, [withPseudo, boardSigner, relaysFor, ingest]);

  /** NIP-09: delete a post — author or board admin only. */
  const deletePost = useCallback(async (boardId: string, targetId: string) => {
    requireSigner();
    const m = withPseudo(boardId);
    const me = boardPkOf(m);
    const post = data[boardId]?.posts.find(p => p.ev.id === targetId);
    if (post && post.ev.pubkey !== me && post.ev.pubkey !== signer?.pubkey && !isAdminPk(m, me ?? '') && signer?.pubkey !== m.admin) {
      throw new Error('Only the author or the board admin can delete a post');
    }
    const ev = await sign(boardSigner(m), buildDelete(m.epochKeys[m.currentEpoch], boardId, m.currentEpoch, targetId));
    await publish(relaysFor(m), ev);
    ingest(boardId, ev, m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signer, withPseudo, boardSigner, data, relaysFor, ingest]);

  const removeMember = useCallback(async (boardId: string, memberPk: string) => {
    const s = requireSigner();
    const m = persisted.boards[boardId];
    if (!m || m.role !== 'admin') throw new Error('Only the board admin can remove members');
    const crypto = signerCrypto(s);
    if (s.type !== 'local' && !crypto) {
      throw new Error('Epoch rotation needs peer encryption — your signer extension has no nip44 support');
    }
    const r = rotateEpoch(m.epochKeys[m.currentEpoch], m.currentEpoch + 1);
    const meta = m.meta ?? { name: 'board', description: '', createdAt: Math.floor(Date.now() / 1000) };
    const def = await sign(s, buildBoardDef(boardId, r.key, meta));
    await publish(relaysFor(m), def);
    // distribute the salt to every remaining roster member (removed member gets nothing).
    // Roster pubkeys are per-board pseudos; map them back to identity keys via the
    // memberIndex built from join payloads (legacy roster entries ARE identities).
    const d = data[boardId];
    const removedReal = m.memberIndex?.[memberPk];
    const myPseudo = boardPkOf(m);
    const members = Object.keys(d?.roster ?? {})
      .filter(pk => pk !== memberPk && pk !== myPseudo && pk !== s.pubkey && pk !== removedReal)
      .map(pk => m.memberIndex?.[pk] ?? pk);
    const sent = new Set<string>();
    for (const pk of members) {
      if (sent.has(pk)) continue;
      sent.add(pk);
      const ku = s.type === 'local'
        ? buildKeyUpdate(s.sk, pk, boardId, r.epoch, r.salt)
        : await buildKeyUpdateAsync(crypto!, t => sign(s, t), s.pubkey, pk, boardId, r.epoch, r.salt);
      await publish(relaysFor(m), ku).catch(() => undefined);
    }
    persist(p => ({
      ...p,
      boards: {
        ...p.boards,
        [boardId]: {
          ...m,
          currentEpoch: r.epoch,
          epochKeys: { ...m.epochKeys, [r.epoch]: r.key },
          // admin keeps the salt per epoch so offline members can resync later
          salts: { ...(m.salts ?? {}), [r.epoch]: r.salt },
          removed: removedReal ? [...new Set([...(m.removed ?? []), removedReal])] : m.removed,
        },
      },
    }));
  }, [signer, persisted.boards, data, persist, relaysFor]);

  /** Member: ask the board admin for the salt chain above my current epoch
   *  (missed rotations while offline). Gift-wrapped from my identity key —
   *  the admin must know who asks; relays see only the wrap. */
  const requestResync = useCallback(async (boardId: string) => {
    const s = requireSigner();
    const m = persistedRef.current.boards[boardId];
    if (!m) throw new Error('Not a member');
    if (m.role === 'admin') throw new Error('You are the board admin — nothing to resync');
    let ev: NostrEvent;
    if (s.type === 'local') {
      ev = buildResyncReq(s.sk, m.admin, boardId, m.currentEpoch);
    } else {
      const crypto = signerCrypto(s);
      if (!crypto) throw new Error('Resync needs peer encryption — your signer extension has no nip44 support');
      ev = await buildResyncReqAsync(crypto, t => sign(s, t), s.pubkey, m.admin, boardId, m.currentEpoch);
    }
    await publish(relaysFor(m), ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signer, relaysFor]);
  requestResyncRef.current = (b: string) => { requestResync(b).catch(() => undefined); };

  /** Report a post to the deployment's moderator pubkey — NIP-56 semantics,
   *  NIP-59 transport (gift-wrapped from the reporter's identity key, so
   *  relays can't see who reported what). Disabled until MODERATOR_PUBKEY is set. */
  const reportPost = useCallback(async (
    boardId: string,
    target: { id: string; pubkey: string },
    reason: string,
    content?: { subject?: string; body?: string },
  ) => {
    const s = requireSigner();
    if (!/^[0-9a-f]{64}$/.test(MODERATOR_PUBKEY)) {
      throw new Error('Reporting is not configured on this deployment (no moderator pubkey set)');
    }
    const payload = {
      t: 'report' as const,
      board: boardId,
      target: target.id,
      author: target.pubkey,
      subject: content?.subject,
      excerpt: content?.body?.replace(/\[[^\]]*\]/g, '').slice(0, 500),
      reason: reason || undefined,
      reportedAt: Math.floor(Date.now() / 1000),
    };
    let ev: NostrEvent;
    if (s.type === 'local') {
      ev = buildReport(s.sk, MODERATOR_PUBKEY, payload);
    } else {
      const crypto = signerCrypto(s);
      if (!crypto) throw new Error('Reporting needs peer encryption — your signer extension has no nip44 support');
      ev = await buildReportAsync(crypto, t => sign(s, t), s.pubkey, MODERATOR_PUBKEY, payload);
    }
    await publish(relays, ev);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signer, relays]);

  /** Admin: publish the board's relay list (kind 30002) and persist it locally. */
  const publishRelayList = useCallback(async (boardId: string, relayList: string[]) => {
    const s = requireSigner();
    const m = persisted.boards[boardId];
    if (!m || m.role !== 'admin') throw new Error('Only the board admin can publish the relay list');
    const updatedAt = Math.floor(Date.now() / 1000);
    const ev = await sign(s, buildRelayList(m.epochKeys[m.currentEpoch], boardId, relayList, updatedAt));
    await publish(relaysFor(m), ev);
    persist(p => ({
      ...p,
      boards: { ...p.boards, [boardId]: { ...m, relays: relayList, relaysUpdatedAt: updatedAt } },
    }));
  }, [signer, persisted.boards, persist, relaysFor]);

  const addRelay = useCallback((url: string) => {
    const u = url.trim();
    if (!u.startsWith('wss://') && !u.startsWith('ws://')) throw new Error('Relay URL must start with wss://');
    persist(p => ({ ...p, relays: [...new Set([...p.relays, u])] }));
  }, [persist]);

  const removeRelay = useCallback((url: string) => {
    persist(p => ({ ...p, relays: p.relays.filter(r => r !== url) }));
  }, [persist]);

  const store: Store = useMemo(() => ({
    signer,
    boards: persisted.boards,
    data,
    profiles,
    relays,
    customRelays: persisted.relays,
    relayHealth,
    publicBoards,
    reports,
    ready,
    locked,
    keyIsPlaintext: signer?.type === 'local' && !!persisted.sk && !isEncryptedKey(persisted.sk),
    loginWithNip07, loginWithNsec, createIdentity, unlockWithPassphrase, encryptStoredKey, logout,
    createBoard, makeInvite, joinWithInvite, joinPublicBoard, postThread, postReply, react, deletePost, removeMember,
    requestResync, reportPost, publishRelayList, addRelay, removeRelay, ensureProfiles,
  }), [signer, persisted.boards, persisted.relays, persisted.sk, data, profiles, relays, relayHealth, publicBoards, reports, ready, locked,
       loginWithNip07, loginWithNsec, createIdentity, unlockWithPassphrase, encryptStoredKey, logout,
       createBoard, makeInvite, joinWithInvite, joinPublicBoard, postThread, postReply, react, deletePost, removeMember,
       requestResync, reportPost, publishRelayList, addRelay, removeRelay, ensureProfiles]);

  return <Ctx.Provider value={store}>{children}</Ctx.Provider>;
}
